// src/components/common/PdfPreviewModal.js
import React, { useEffect, useRef } from 'react';
import { X, Download } from 'lucide-react';

const PdfPreviewModal = ({ isOpen, onClose, pdfData, title }) => {
  const modalRef = useRef(null);
  
  // Close modal when Escape key is pressed
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      // Prevent body scrolling when modal is open
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      // Restore body scrolling when modal is closed
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, onClose]);
  
  // Close the modal when clicking outside the content
  const handleBackdropClick = (e) => {
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      onClose();
    }
  };
  
  // Handle document download
  const handleDownload = () => {
    if (!pdfData) return;
    
    // Convert base64 to Blob for download
    const byteCharacters = atob(pdfData);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/pdf' });
    
    // Create URL for the blob
    const blobUrl = URL.createObjectURL(blob);
    
    // Determine filename from title or use a generic one
    let filename = 'document.pdf';
    if (title) {
      // Replace spaces and special characters with dashes
      filename = title.replace(/\s+/g, '-').replace(/[^a-zA-Z0-9-_.]/g, '') + '.pdf';
    }
    
    // Create download link
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Cleanup
    setTimeout(() => URL.revokeObjectURL(blobUrl), 30000);
  };
  
  if (!isOpen) return null;
  
  return (
    <div 
      className="fixed inset-0 flex items-center justify-center z-50 p-4 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity duration-300"
      onClick={handleBackdropClick}
    >
      <div 
        ref={modalRef}
        className="w-full max-w-5xl h-[85vh] bg-white rounded-lg shadow-xl flex flex-col overflow-hidden transform transition-all duration-300 ease-in-out animate-modal-appear"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h3 className="text-lg font-medium text-gray-900">{title || 'Document Preview'}</h3>
          <button
            className="text-gray-400 hover:text-gray-500 focus:outline-none transition-colors duration-150 ease-in-out"
            onClick={onClose}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* PDF content */}
        <div className="flex-grow p-0 bg-gray-100 overflow-hidden">
          {pdfData ? (
            <iframe 
              src={`data:application/pdf;base64,${pdfData}`}
              title="PDF Viewer"
              className="w-full h-full border-0"
            />
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-gray-500">No PDF data available</div>
            </div>
          )}
        </div>
        
        {/* Modal footer */}
        <div className="flex items-center justify-end px-6 py-3 bg-gray-50 border-t border-gray-200">
          <button
            className="px-4 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors duration-150 ease-in-out mr-3"
            onClick={onClose}
          >
            Close
          </button>
          <button
            onClick={handleDownload}
            disabled={!pdfData}
            className="px-4 py-2 bg-indigo-600 border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors duration-150 ease-in-out flex items-center disabled:opacity-50"
          >
            <Download className="h-4 w-4 mr-2" />
            Download
          </button>
        </div>
      </div>
    </div>
  );
};

export default PdfPreviewModal;